import type { HtmlEditApplierDeps, HtmlEditApplierMode } from './shared';
import { getBlockCardAnchor } from './shared';
import { getIsPdfLocationAnchor } from '../../extract-paragraphs/pdf/locationAnchor';
import {
  AlineTransforms,
  applyRemoveTableOutput,
  generateSuggestedEditGroupId,
  parseAnchorId,
} from '../../slate-api';
import type { RemoveTableSuggestion } from '../redline-llm-output';
import type {
  CodeBlockRedlineEditError,
  CodeBlockRedlineItem,
  CodeBlockRedlineRemove,
} from '../redline-shapes';

/**
 * Apply one streamed `remove-table` suggestion to the server-side editor and
 * convert it into the `CodeBlockRedlineItem` (a `redline-remove` or a
 * `redline-edit-error`) that the client will render. The table's own
 * `table-…` id identifies it; every row is marked for removal and accepting
 * removes the table itself.
 *
 * The card carries no dependency fingerprint on purpose: removing a whole
 * table is intentional regardless of later edits to its contents, so content
 * changes must not invalidate a deferred removal.
 */
export async function applyRemoveTableSuggestion(
  suggest: RemoveTableSuggestion,
  deps: HtmlEditApplierDeps,
  mode: HtmlEditApplierMode
): Promise<CodeBlockRedlineItem> {
  const { editorPool, errorReporter, logger, makeSource, paragraphState, userId } = deps;
  const { documentId, id: anchorId, reasoning } = suggest;
  const groupId = generateSuggestedEditGroupId();

  const removeTableError = (message: string): CodeBlockRedlineEditError => ({
    documentId,
    groupId,
    locationAnchor: anchorId,
    message,
    type: 'redline-edit-error',
  });

  const parsed = parseAnchorId(anchorId);
  if (parsed.kind !== 'table') {
    const message = `RedlineAgent remove-table requires the table's own "table-…" id, got "${anchorId}" (a ${parsed.kind} id).`;
    logger.error(message);
    errorReporter.captureMessage(message, { extra: { anchorId, documentId }, level: 'error' });
    return removeTableError(message);
  }

  const blockAnchorInfo = await paragraphState.getBlockAnchorInfoByAnchorIdWithLazyLoad(
    documentId,
    anchorId
  );
  if (!blockAnchorInfo) {
    const message = `RedlineAgent could not find table "${anchorId}" in document "${documentId}" to remove.`;
    logger.error(message);
    const allAnchors = paragraphState.getAllLocationAnchorsForDocument(documentId);
    errorReporter.captureMessage(message, {
      extra: {
        anchorId,
        availableAnchorsCount: allAnchors.length,
        availableAnchorsSample: allAnchors.slice(0, 50),
        documentId,
      },
      level: 'error',
    });
    return removeTableError(message);
  }

  /**
   * Preview mode returns the card without applying. PDF (Apryse) documents
   * are read-only with no table structure in the collab editor — the card is
   * anchored on the table's own coordinates — and signing-locked documents
   * (sent for signature or signed) must not receive new edits: both always
   * take this path.
   */
  if (
    mode === 'preview' ||
    getIsPdfLocationAnchor(blockAnchorInfo.locationAnchor) ||
    (await editorPool.getSigningLockState(documentId))
  ) {
    const cardAnchor = getBlockCardAnchor(blockAnchorInfo);
    return {
      documentId,
      endLocationAnchor: cardAnchor,
      groupId,
      html: blockAnchorInfo.pdfPreviewHtml ?? '',
      locationAnchor: cardAnchor,
      reasoning,
      type: 'redline-remove',
    } satisfies CodeBlockRedlineRemove;
  }

  let removedHtml = '';
  try {
    const editor = await editorPool.getOrCreateEditor(documentId);
    AlineTransforms.withoutNormalizing(editor, () => {
      const result = applyRemoveTableOutput(editor, {
        authorId: userId,
        groupId,
        locationAnchor: anchorId,
        reasoning,
        source: makeSource(groupId),
      });
      removedHtml = result.removedHtml;
    });
  } catch (error) {
    const message =
      error instanceof Error
        ? `RedlineAgent applyRemoveTableOutput threw for table "${anchorId}": ${error.message}`
        : `RedlineAgent applyRemoveTableOutput threw for table "${anchorId}".`;
    logger.error(message, error);
    errorReporter.captureException(error, { extra: { anchorId, documentId } });
    return removeTableError(message);
  }

  const cardAnchor = getBlockCardAnchor(blockAnchorInfo);
  return {
    documentId,
    endLocationAnchor: cardAnchor,
    groupId,
    html: removedHtml,
    locationAnchor: cardAnchor,
    reasoning,
    type: 'redline-remove',
  } satisfies CodeBlockRedlineRemove;
}
